import { useState, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { authorizationHeader } from '../lib/api'

type Stage = 'idle' | 'uploading' | 'done' | 'error'

export function Publish() {
  const { slug = '' } = useParams()
  const navigate        = useNavigate()
  const fileRef         = useRef<HTMLInputElement>(null)
  const [file, setFile]       = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [form, setForm]       = useState({ name: '', provider: 'aws', version: '' })
  const [stage, setStage]     = useState<Stage>('idle')
  const [progress, setProgress] = useState(0)
  const [error, setError]     = useState<string | null>(null)

  const acceptFile = useCallback((f: File) => {
    if (!f.name.endsWith('.tar.gz') && !f.name.endsWith('.tgz')) {
      setError('File must be a .tar.gz archive.')
      return
    }
    setFile(f)
    setError(null)
    // Auto-fill name from filename pattern: name-provider-version.tar.gz
    const match = f.name.match(/^([^-]+)-([^-]+)-([\d.]+)\.tar\.gz$/)
    if (match) {
      setForm((prev) => ({
        ...prev,
        name: match[1],
        provider: match[2],
        version: match[3],
      }))
    }
  }, [])

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) acceptFile(f)
  }

  async function handlePublish(e: React.FormEvent) {
    e.preventDefault()
    if (!file) { setError('Please select a .tar.gz file.'); return }
    setError(null)
    setStage('uploading')

    try {
      await uploadWithProgress(
        `/v1/modules/${slug}/${form.name}/${form.provider}/${form.version}`,
        file,
        setProgress,
        await authorizationHeader(),
      )
      setStage('done')
    } catch (err) {
      setError((err as Error).message)
      setStage('error')
    }
  }

  return (
    <Layout
      title="Publish module"
      actions={
        <button className="btn btn-ghost" onClick={() => navigate(`/namespaces/${slug}`)}>
          ← Back
        </button>
      }
    >
      <div style={{ maxWidth: 560 }}>
        {stage === 'done' ? (
          <div className="card fu">
            <div className="card-body" style={{ textAlign: 'center', padding: '40px 24px' }}>
              <div style={{ fontSize: '2rem', marginBottom: 12 }}>✓</div>
              <div style={{ color: 'var(--green)', fontFamily: 'var(--mono)', marginBottom: 8 }}>
                v{form.version} published
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text2)', marginBottom: 20 }}>
                {slug}/{form.name}/{form.provider}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setStage('idle')
                    setFile(null)
                    setProgress(0)
                    setForm((f) => ({ ...f, version: '' }))
                  }}
                >
                  Publish another
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/namespaces/${slug}/modules/${form.name}/${form.provider}`)}
                >
                  View module →
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handlePublish}>
            {/* Drop zone */}
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              style={{
                border: `2px dashed ${dragging ? 'var(--accent)' : file ? 'var(--green)' : 'var(--border2)'}`,
                borderRadius: 'var(--radius-lg)',
                padding: '36px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                marginBottom: 20,
                transition: 'border-color 0.15s',
                background: dragging ? 'var(--accent-d)' : 'transparent',
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".tar.gz,.tgz"
                style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) acceptFile(f) }}
              />
              {file ? (
                <div>
                  <div style={{ color: 'var(--green)', fontFamily: 'var(--mono)', fontSize: '0.85rem', marginBottom: 4 }}>
                    {file.name}
                  </div>
                  <div style={{ color: 'var(--text3)', fontSize: '0.75rem' }}>
                    {(file.size / 1024).toFixed(1)} KB — click to change
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>⬡</div>
                  <div style={{ color: 'var(--text2)', fontSize: '0.85rem', marginBottom: 4 }}>
                    Drop your <span style={{ fontFamily: 'var(--mono)' }}>.tar.gz</span> here
                  </div>
                  <div style={{ color: 'var(--text3)', fontSize: '0.75rem' }}>or click to browse</div>
                </div>
              )}
            </div>

            {/* Module fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <label className="field-label">Module name</label>
                <input
                  className="input"
                  placeholder="vpc"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase() })}
                  required
                  pattern="[a-z][a-z0-9\-_]{0,63}"
                />
              </div>
              <div>
                <label className="field-label">Provider</label>
                <input
                  className="input"
                  placeholder="aws"
                  value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.target.value.toLowerCase() })}
                  required
                  pattern="[a-z][a-z0-9\-_]{0,63}"
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label className="field-label">Version (semver)</label>
                <input
                  className="input"
                  placeholder="1.0.0"
                  value={form.version}
                  onChange={(e) => setForm({ ...form, version: e.target.value })}
                  required
                  pattern="\d+\.\d+\.\d+(-[a-zA-Z0-9.]+)?"
                />
              </div>
            </div>

            {error && (
              <div className="feedback-error" style={{ marginBottom: 16 }}>{error}</div>
            )}

            {stage === 'uploading' && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${progress}%`,
                      background: 'var(--accent)',
                      transition: 'width 0.1s',
                    }}
                  />
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text3)', marginTop: 6 }}>
                  Uploading… {progress}%
                </div>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={stage === 'uploading'}
            >
              {stage === 'uploading' ? <span className="spinner" /> : 'Publish'}
            </button>
          </form>
        )}
      </div>
    </Layout>
  )
}

function uploadWithProgress(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
  authorizationHeader: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.setRequestHeader('Authorization', authorizationHeader)
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`Publish failed: ${xhr.status}`))
    })
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')))
    xhr.send(file)
  })
}
